import Modal from "@/components/Modal";
import Spacer from "@/components/Spacer";
import { useCallback, useEffect, useState } from "react";
import useNoodleApi from "@/hooks/useNoodleApi";
import { getExtractedData } from "@/tsClient/documentRequests";
import Shimmer from "@/components/Shimmer";
import Tabs from "@/components/DesignLibrary/Tabs";
import { Table, TableRow, TBody, TD, TH, THead } from "@/components/DesignLibrary/Table";
import s from './DocumentPreview.module.scss';
import PrettyJson from "./PrettyJson";

type Props = {
  onClose: () => void;
  documentId: string;
};

const DEFAULT_IMAGE_JSON_DATA = {
  document: 'no extracted data was found',
};

type ExtractedData = {
  internalData: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
  }
};

const ExtractedDataModal: React.FC<Props> = ({ onClose, documentId }) => {

  const [extractedData, setExtractedData] = useState<ExtractedData>();
  const [reasoning, setReasoning] = useState<Record<string, unknown>>({});
  const { getData: getExtractedDataFn, fetchingState: { isFetching } } = useNoodleApi(getExtractedData);
  const [selectedSection, setSelectedSection] = useState<'data' | 'reasoning'>('reasoning');

  const fetchExtractedData = useCallback(async (): Promise<void> => {
    const res = await getExtractedDataFn({
      documentId,
    });
    if (res.data) {
      setExtractedData(res.data);
    }
  }, [documentId, getExtractedDataFn]);

  const extractReasons = (input: Record<string, unknown>): Record<string, unknown> =>
    Object.entries(input).reduce<Record<string, unknown>>((acc, [key, value]) => {
      if (key.endsWith('__Reason')) {
        const baseKey = key.slice(0, -8);
        acc[baseKey] = value;
      }
      return acc;
    }, {});

  useEffect(() => {
    if (documentId) {
      fetchExtractedData();
    }
  }, [documentId, fetchExtractedData]);

  useEffect(() => {
    if (extractedData?.internalData?.data?.tableData) {
      setReasoning(extractReasons(extractedData.internalData.data.tableData as Record<string, unknown>));
    } else setReasoning({});
  }, [extractedData]);

  return (
    <Modal
      hideBottomInset
      title="Data extracted by AI"
      onClose={onClose}
      drag
      isWide={Boolean(Object.keys(reasoning).length)}
      align="left"
    >
      {isFetching
        ? <div className={s['loader-container']}>
          <Shimmer className={s.shimmer} />
          <Spacer />
          <Shimmer className={s.shimmer} />
        </div>
        : <>
          <div
            style={{
              padding: 24,
            }}
          >
            <Tabs
              inputName="reasoningOrData"
              inputs={[
                { id: 'reasoning', label: 'Reasoning' },
                { id: 'data', label: 'Json Data' },
              ]}
              checked={selectedSection}
              onChange={setSelectedSection}
            />

            {selectedSection === 'reasoning'
              ? <>

                {Object.keys(reasoning).length
                  ? <>
                    <Table className="caption">
                      <THead>
                        <TableRow>
                          <TH>Field</TH>
                          <TH>Value</TH>
                          <TH>Reasoning</TH>
                        </TableRow>
                      </THead>
                      <TBody>
                        {Object.keys(reasoning).map((key) => (
                          <TableRow key={key}>
                            <TD>{key}</TD>
                            <TD>{(extractedData?.internalData?.data?.tableData as Record<string, string>)[key] ?? '--'}</TD>
                            <TD>
                              {reasoning[key]}
                            </TD>
                          </TableRow>
                        ))}
                      </TBody>
                    </Table>
                  </>
                  : <div className={'caption'} style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', padding: "40px 16px 16px 16px" }}>No data</div>}
              </>
              : <>
                <Spacer />
                <PrettyJson
                  stringifiedJson={JSON.stringify((extractedData ?? DEFAULT_IMAGE_JSON_DATA))}
                />
              </>
            }
          </div>
        </>}
    </Modal>
  );
};

export default ExtractedDataModal;
